// A Bloom-style echo garden: every note you play is pinned to the nearest grid
// step and re-fires once per cycle, each repeat quieter and cloudier than the
// last, until it fades away. No buttons, no record/close — just play and leave.

import type { VoicePreset } from '../domain/types'

export interface EchoSeed {
  slot: number
  midi: number
  velocity: number
  timbre: number
  pan: number
  // The voice the note was played with, so a repeat never changes instrument.
  preset?: VoicePreset
}

export interface EchoFire extends EchoSeed {
  when: number
  generation: number
  gain: number
}

export interface EchoGardenOptions {
  bpm?: number
  stepsPerBeat?: number
  beatsPerCycle?: number
  generations?: number
  decay?: number
  capacity?: number
}

interface PlantedEcho {
  seed: EchoSeed
  step: number
  plantedCycle: number
  lastFiredGeneration: number
}

export class EchoGarden {
  readonly cycleSeconds: number
  readonly stepSeconds: number
  readonly generations: number
  private readonly decay: number
  private readonly capacity: number
  private readonly stepsPerCycle: number
  private origin: number | null = null
  private planted: PlantedEcho[] = []

  constructor(options: EchoGardenOptions = {}) {
    const bpm = options.bpm ?? 90
    const stepsPerBeat = options.stepsPerBeat ?? 2
    const beatsPerCycle = options.beatsPerCycle ?? 8
    this.stepSeconds = 60 / bpm / stepsPerBeat
    this.stepsPerCycle = stepsPerBeat * beatsPerCycle
    this.cycleSeconds = this.stepSeconds * this.stepsPerCycle
    this.generations = Math.max(1, options.generations ?? 4)
    this.decay = options.decay ?? 0.6
    this.capacity = Math.max(1, options.capacity ?? 32)
  }

  get size(): number { return this.planted.length }

  // Phase of the current cycle, 0..1, for visual pulses. Null before the first note.
  phase(nowSeconds: number): number | null {
    if (this.origin === null) return null
    const elapsed = nowSeconds - this.origin
    return ((elapsed % this.cycleSeconds) + this.cycleSeconds) % this.cycleSeconds / this.cycleSeconds
  }

  add(seed: EchoSeed, nowSeconds: number): void {
    if (this.origin === null) this.origin = nowSeconds
    const elapsed = nowSeconds - this.origin
    const absoluteStep = Math.round(elapsed / this.stepSeconds)
    const plantedCycle = Math.floor(absoluteStep / this.stepsPerCycle)
    const step = absoluteStep - plantedCycle * this.stepsPerCycle
    this.planted.push({ seed: { ...seed }, step, plantedCycle, lastFiredGeneration: 0 })
    while (this.planted.length > this.capacity) this.planted.shift()
  }

  // Returns every repeat whose time falls inside [fromSeconds, toSeconds). Repeats
  // earlier than the window are counted as missed so they never fire late.
  collect(fromSeconds: number, toSeconds: number): EchoFire[] {
    if (this.origin === null) return []
    const fires: EchoFire[] = []
    for (const echo of this.planted) {
      for (let generation = echo.lastFiredGeneration + 1; generation <= this.generations; generation += 1) {
        const when = this.origin + (echo.plantedCycle + generation) * this.cycleSeconds + echo.step * this.stepSeconds
        if (when >= toSeconds) break
        echo.lastFiredGeneration = generation
        if (when < fromSeconds) continue
        fires.push({ ...echo.seed, when, generation, gain: echo.seed.velocity * this.decay ** generation })
      }
    }
    this.planted = this.planted.filter((echo) => echo.lastFiredGeneration < this.generations)
    return fires.sort((a, b) => a.when - b.when)
  }

  clear(): void {
    this.planted = []
  }
}
